import React from "react";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Tooltip from "@mui/material/Tooltip";
import CardMedia from "@mui/material/CardMedia";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { tooltipStyles } from "../IconBanner.styles";
import { channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";

const MockIconBg = () => (
  <svg viewBox="0 0 261 261" xmlns="http://www.w3.org/2000/svg" style={{
    position: 'absolute',
top: 0,
left: 0,
right: 0,
bottom: 0,
    paddingTop: "1.3px",
    zIndex: 1
  }}>
    <g><circle cx="108" cy="108" data-name="Ellipse 34" fill="#747474" r="108" transform="translate(22.5 20.5)"/></g>
  </svg>
);

interface AddOnIconCategoryProps {
  ctgValue: string;
  channels: any[];
}

const AddOnIconCategory = ({
  ctgValue,
  channels
}: AddOnIconCategoryProps) => (
    <DtvBox
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "100%",
        minWidth: "100%",
        paddingTop: {
 xs: "28px",
sm: "16px",
md: "40px"
}
    }}>
      <DtvText
        content={ctgValue.toUpperCase()}
        variant="body2medium"
        sx={{
          paddingBottom: "8px",
          fontSize: {
 xs: "16px",
md: "20px"
},
          color: colorPalette.neutral_white.main,
          lineHeight: {
 xs: "20.8px",
md: "24px"
},
          letterSpacing: { md: "-0.48px" },
          fontWeight: 500
        }}
      />
      <DtvBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          marginLeft: "-5px", // left-align with category header (DAM img's have transparent space)
          overflowY: "hidden",
          width: '100%',
          maxHeight: "5000px",
          transition: "height 300ms ease-in-out 100ms",
          marginTop: {
 xs: "3px",
sm: "1px"
} // 8px - 2px (icon border) = 6px - [xs:3, sm:5, md:5]
      }}>
        {channels.map((channel, index) => (
            <Tooltip
              describeChild
              enterTouchDelay={0}
              key={`AddOn-${channel?.name}-${channel?.chnlNum}`}
              placement="bottom"
              title={channel?.name}
              PopperProps={{
                disablePortal: true,
                modifiers: [
                // {
                //   name: 'preventOverflow',
                //   enabled: true,
                //   phase: 'main',
                // },
                // {
                //   name: 'flip',
                //   enabled: true,
                //   phase: 'main',
                // },
                {
                  name: 'offset',
                  options: {
                    offset: [0, -20]
                  }
                }],
                sx: { width: 'unset !important' }
              }}
              componentsProps={{
                tooltip: { sx: tooltipStyles.tooltip },
                arrow: { sx: tooltipStyles.arrow  }
              }}
              sx={{
                "& #popper[data-popper-escaped]": {
                  visibility: 'hidden'
                }
              }}>
              {channel?.icon && (
                // [WAT-12043 / YFH-AMP112] Was <a role="button" tabIndex="0">
                // wired to a no-op stub in the parent (see handleIconClick
                // removal in IconBanner_wAddOnChnls.tsx). Now a plain
                // presentational chip — clicks / focus / hover-press states
                // no longer imply an interaction that doesn't exist. Channel
                // names still reach screen readers via the CardMedia alt.
                <DtvBox
                  id={`add-on-icon-container-${channel?.category}-${index}`}
                  sx={{
                    backgroundColor: "transparent",
                    // WAT-11362: flag ON the chip draws its own badge + shadow, so keeping this
                    // one too double-stacks them. Flag OFF it must stay exactly as production.
                    ...(channel?.isOvLogo ? {} : { boxShadow: `0px 2px 20px 0px rgba(0, 0, 0, 0.05)` }),
                    ...channelLogoChipFor(channel?.isOvLogo, channel?.icon),
                    borderRadius: "100%",
                    position: "relative",
                    width: {
                      xs: "74px !important",
                      sm: "74px !important",
                      md: "122px !important"
                    },
                    padding: "0px",
                    margin: {
                      xs: "0 3px 4.5px 0",
                      sm: "0 3px 4.5px 0",
                      md: "0 4px 6px 0"
                    }
                  }}>
                      {/* WAT-11362: flag ON the logo carries its own white badge, so this grey
                        disc would show through transparent artwork as a second ring. */}
                    {!channel?.isOvLogo && <MockIconBg />}
                    <CardMedia
                        alt={channel?.name}
                        component="img" loading="lazy"
                        src={channel.icon}
                        sx={{
                          paddingTop: "1.3px", // eyeballed to center svg exactly
                          ...channelLogoSx(channel?.isOvLogo, channel?.icon),
                          fontSize: "8px", // for error text
                          color: colorPalette.neutral_white.main,
                          contentVisibility: "auto",
                          position: "relative",
                          zIndex: 2
                        }}
                        onError={e => {
                          console.warn("[AddOnIconCategory] channel icon not found, displaying fallback", channel);
                          // WAT-11362: an OV imageURL can 404; hiding the element left an empty
                          // chip, so an OV logo falls back to the no-logo asset. A non-OV logo
                          // replays the original hide-parent behaviour.
                          handleChannelLogoError(e, channel?.isOvLogo, "hideParent");
                        }}
                      />
                </DtvBox>
              )}
            </Tooltip>
          ))}
      </DtvBox>
    </DtvBox>
);

export default React.memo(AddOnIconCategory);
